import dayjs, { Dayjs } from "dayjs";

interface ComparisonTableItem {
  id: string;
  shared_uid: string | null;
  table?: string;
  active?: boolean;
  from_date: Dayjs | null;
  to_date: Dayjs | null;
  page_group_to_comparison_table_shared_uid?: string;
  isExisting: boolean;
  original_shared_uid?: string;
  original_from_date?: string;
  original_to_date?: string;
  original_table_name?: string;
  selectedValue?: any;
  isUnlinked?: boolean;
  beforeUnlinkState?: {
    shared_uid: string | null;
    from_date: Dayjs | null;
    to_date: Dayjs | null;
    selectedValue?: any;
    table?: string;
  };
}

interface TrafficSourceItem {
  id: string;
  shared_uid: string | null;
  source?: string;
  active?: boolean;
  from_date: Dayjs | null;
  to_date: Dayjs | null;
  page_group_to_traffic_source_shared_uid?: string;
  isExisting: boolean;
  original_shared_uid?: string;
  original_from_date?: string;
  original_to_date?: string;
  original_source_name?: string;
  selectedValue?: any;
  isUnlinked?: boolean;
  beforeUnlinkState?: {
    shared_uid: string | null;
    from_date: Dayjs | null;
    to_date: Dayjs | null;
    selectedValue?: any;
    source?: string;
  };
}

interface ComparisonTablePayload {
  create_new?: Array<{
    comparison_table_shared_uid: string;
    start_date: string;
    end_date: string;
  }>;
  change_link?: Array<{
    page_group_to_comparison_table_shared_uid: string;
    comparison_table_shared_uid: string;
    start_date: string;
    end_date: string;
  }>;
  change_date?: Array<{
    page_group_to_comparison_table_shared_uid: string;
    start_date: string;
    end_date: string;
  }>;
  unlink?: Array<{
    page_group_to_comparison_table_shared_uid: string; // CHANGED: Use junction table UID
  }>;
}

interface TrafficSourcePayload {
  create_new?: Array<{
    traffic_source_shared_uid: string;
    start_date: string;
    end_date: string;
  }>;
  change_link?: Array<{
    page_group_to_traffic_source_shared_uid: string;
    traffic_source_shared_uid: string;
    start_date: string;
    end_date: string;
  }>;
  change_date?: Array<{
    page_group_to_traffic_source_shared_uid: string;
    start_date: string;
    end_date: string;
  }>;
  unlink?: Array<{
    page_group_to_traffic_source_shared_uid: string;
  }>;
}

/**
 * Builds the comparison tables payload for API submission
 * @param comparisonTableList - Array of comparison table items
 * @returns Structured payload with create_new, change_link, change_date, and unlink arrays
 */
export const buildComparisonTablesPayload = (
  comparisonTableList: ComparisonTableItem[]
): ComparisonTablePayload => {
  const createNew: ComparisonTablePayload["create_new"] = [];
  const changeLink: ComparisonTablePayload["change_link"] = [];
  const changeDate: ComparisonTablePayload["change_date"] = [];
  const unlink: ComparisonTablePayload["unlink"] = [];

  comparisonTableList.forEach((item) => {
    if (
      item.isUnlinked &&
      item.isExisting &&
      item.page_group_to_comparison_table_shared_uid
    ) {
      unlink.push({
        page_group_to_comparison_table_shared_uid:
          item.page_group_to_comparison_table_shared_uid,
      });
      return; // Skip other processing for unlinked items
    }

    if (!item.shared_uid) return; // Skip empty items

    const startDate = item.from_date
      ? item.from_date.format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const endDate = item.to_date
      ? item.to_date.format("YYYY-MM-DD")
      : "2099-12-31";

    if (!item.isExisting) {
      // New item - add to create_new
      createNew.push({
        comparison_table_shared_uid: item.shared_uid,
        start_date: startDate,
        end_date: endDate,
      });
    } else if (item.original_shared_uid !== item.shared_uid) {
      // Changed table (link changed) - add to change_link
      changeLink.push({
        page_group_to_comparison_table_shared_uid:
          item.page_group_to_comparison_table_shared_uid!,
        comparison_table_shared_uid: item.shared_uid,
        start_date: startDate,
        end_date: endDate,
      });
    } else if (
      item.from_date?.format("YYYY-MM-DD") !== item.original_from_date ||
      item.to_date?.format("YYYY-MM-DD") !== item.original_to_date
    ) {
      // Only dates changed - add to change_date
      changeDate.push({
        page_group_to_comparison_table_shared_uid:
          item.page_group_to_comparison_table_shared_uid!,
        start_date: startDate,
        end_date: endDate,
      });
    }
  });

  return {
    ...(createNew.length > 0 && { create_new: createNew }),
    ...(changeLink.length > 0 && { change_link: changeLink }),
    ...(changeDate.length > 0 && { change_date: changeDate }),
    ...(unlink.length > 0 && { unlink: unlink }),
  };
};

/**
 * Builds the traffic sources payload for API submission
 * @param trafficSourceList - Array of traffic source items
 * @returns Structured payload with create_new, change_link, change_date, and unlink arrays
 */
export const buildTrafficSourcesPayload = (
  trafficSourceList: TrafficSourceItem[]
): TrafficSourcePayload => {
  const createNew: TrafficSourcePayload["create_new"] = [];
  const changeLink: TrafficSourcePayload["change_link"] = [];
  const changeDate: TrafficSourcePayload["change_date"] = [];
  const unlink: TrafficSourcePayload["unlink"] = [];

  trafficSourceList.forEach((item) => {
    if (
      item.isUnlinked &&
      item.isExisting &&
      item.page_group_to_traffic_source_shared_uid
    ) {
      unlink.push({
        page_group_to_traffic_source_shared_uid:
          item.page_group_to_traffic_source_shared_uid,
      });
      return; // Skip other processing for unlinked items
    }

    if (!item.shared_uid) return; // Skip empty items

    const startDate = item.from_date
      ? item.from_date.format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const endDate = item.to_date
      ? item.to_date.format("YYYY-MM-DD")
      : "2099-12-31";

    if (!item.isExisting) {
      // New item - add to create_new
      createNew.push({
        traffic_source_shared_uid: item.shared_uid,
        start_date: startDate,
        end_date: endDate,
      });
    } else if (item.original_shared_uid !== item.shared_uid) {
      // Changed source (link changed) - add to change_link
      changeLink.push({
        page_group_to_traffic_source_shared_uid:
          item.page_group_to_traffic_source_shared_uid!,
        traffic_source_shared_uid: item.shared_uid,
        start_date: startDate,
        end_date: endDate,
      });
    } else if (
      item.from_date?.format("YYYY-MM-DD") !== item.original_from_date ||
      item.to_date?.format("YYYY-MM-DD") !== item.original_to_date
    ) {
      // Only dates changed - add to change_date
      changeDate.push({
        page_group_to_traffic_source_shared_uid:
          item.page_group_to_traffic_source_shared_uid!,
        start_date: startDate,
        end_date: endDate,
      });
    }
  });

  return {
    ...(createNew.length > 0 && { create_new: createNew }),
    ...(changeLink.length > 0 && { change_link: changeLink }),
    ...(changeDate.length > 0 && { change_date: changeDate }),
    ...(unlink.length > 0 && { unlink: unlink }),
  };
};

/**
 * Check if an object has any keys.
 */
export const hasData = (obj: any): boolean => {
  return obj && Object.keys(obj).length > 0;
};

/**
 * Validate all comparison table entries.
 * Each entry must have a shared_uid (selected table) and valid from/to dates.
 * Skip validation for unlinked items.
 */
export const isComparisonTableListValid = (list: any[]): boolean => {
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.every((item) => {
    // Skip validation for unlinked items
    if (item.isUnlinked) return true;

    return (
      item.shared_uid &&
      item.from_date &&
      item.to_date &&
      dayjs(item.from_date).isValid() &&
      dayjs(item.to_date).isValid()
    );
  });
};

/**
 * Validate all traffic source entries.
 * Each entry must have a shared_uid (selected source) and valid from/to dates.
 * Skip validation for unlinked items.
 */
export const isTrafficSourceListValid = (list: any[]): boolean => {
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.every((item) => {
    // Skip validation for unlinked items
    if (item.isUnlinked) return true;

    return (
      item.shared_uid &&
      item.from_date &&
      item.to_date &&
      dayjs(item.from_date).isValid() &&
      dayjs(item.to_date).isValid()
    );
  });
};

/**
 * Build filtered payload for page group links.
 * Only includes non-empty comparison_tables and traffic_sources.
 */
export const buildFilteredPayload = (
  compTables: any[],
  trafficSources: any[],
  buildComparisonTablesPayload: Function,
  buildTrafficSourcesPayload: Function
) => {
  const payload: any = {};

  const compTablesData = buildComparisonTablesPayload(compTables);
  const trafficSourcesData = buildTrafficSourcesPayload(trafficSources);

  if (hasData(compTablesData)) payload.comparison_tables = compTablesData;
  if (hasData(trafficSourcesData)) payload.traffic_sources = trafficSourcesData;

  return payload;
};

/**
 * Restore original state of an item before unlinking
 * This saves the current state (including any changes) and prepares for unlinking
 * When user clicks unlink, we want to:
 * 1. Save their current changes (in beforeUnlinkState)
 * 2. Show the original backend values (for display purposes)
 * 3. Mark as unlinked
 */
export const restoreOriginalState = (
  item: ComparisonTableItem | TrafficSourceItem
) => {
  // Save current state before reverting (so we can restore to it later)
  const beforeUnlinkState = {
    shared_uid: item.shared_uid,
    from_date: item.from_date,
    to_date: item.to_date,
    selectedValue: item.selectedValue,
    ...("table" in item && { table: item.table }),
    ...("source" in item && { source: item.source }),
  };

  // Create a selectedValue object with original data for display in autocomplete
  let originalSelectedValue = null;
  if (item.original_shared_uid) {
    if ("original_table_name" in item && item.original_table_name) {
      // For comparison tables - use the stored original name
      originalSelectedValue = {
        shared_uid: item.original_shared_uid,
        widget_name: item.original_table_name,
        table: item.original_table_name,
      };
    } else if ("original_source_name" in item && item.original_source_name) {
      // For traffic sources - use the stored original name
      originalSelectedValue = {
        shared_uid: item.original_shared_uid,
        traffic_source_name: item.original_source_name,
        source: item.original_source_name,
      };
    }
  }

  return {
    ...item,
    // Revert to original values for display
    shared_uid: item.original_shared_uid || item.shared_uid,
    from_date: item.original_from_date
      ? dayjs(item.original_from_date)
      : item.from_date,
    to_date: item.original_to_date
      ? dayjs(item.original_to_date)
      : item.to_date,
    // Revert table/source name to original
    ...("original_table_name" in item && { table: item.original_table_name }),
    ...("original_source_name" in item && {
      source: item.original_source_name,
    }),
    // Set selectedValue to show original in autocomplete
    selectedValue: originalSelectedValue,
    // Save the state before unlinking
    beforeUnlinkState,
    isUnlinked: true,
  };
};

/**
 * Restore an unlinked item back to its state BEFORE unlinking
 * This restores the changes the user had made before they clicked unlink
 */
export const restoreUnlinkedItem = (
  item: ComparisonTableItem | TrafficSourceItem
) => {
  // If we have a saved state before unlinking, restore to that
  if (item.beforeUnlinkState) {
    return {
      ...item,
      shared_uid: item.beforeUnlinkState.shared_uid,
      from_date: item.beforeUnlinkState.from_date,
      to_date: item.beforeUnlinkState.to_date,
      selectedValue: item.beforeUnlinkState.selectedValue,
      ...("table" in item.beforeUnlinkState && {
        table: item.beforeUnlinkState.table,
      }),
      ...("source" in item.beforeUnlinkState && {
        source: item.beforeUnlinkState.source,
      }),
      isUnlinked: false,
      beforeUnlinkState: undefined, // Clear the saved state
    };
  }

  // Fallback: just toggle isUnlinked flag
  return {
    ...item,
    isUnlinked: false,
  };
};
